import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';

// PNG to JPG — runs entirely in the browser using a canvas. No server.
// Transparent areas are filled white (JPG has no transparency).
export default function PngToJpg() {
  const [file, setFile]       = useState(null);
  const [quality, setQuality] = useState(0.92);
  const [result, setResult]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  const onFileChange = f => {
    setError(''); setResult(null);
    if (!f) { setFile(null); return; }
    if (!f.type.includes('png')) { setError('Please select a PNG image.'); setFile(null); return; }
    if (f.size > 20 * 1024 * 1024) { setError('File too large. Max 20MB.'); setFile(null); return; }
    setFile(f);
  };

  const convert = () => {
    if (!file) { setError('Please select a PNG file first.'); return; }
    setBusy(true); setError('');
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        blob => {
          if (!blob) { setError('Conversion failed. Try another image.'); setBusy(false); return; }
          setResult({ url: URL.createObjectURL(blob), size: blob.size });
          setBusy(false);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => { setError('Could not read the image.'); setBusy(false); };
    img.src = url;
  };

  const reset = () => { setFile(null); setResult(null); setError(''); };
  const fmtSize = b => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(2)} MB`;

  return (
    <>
    <Seo title="PNG to JPG Converter Free" description="Convert PNG images to smaller JPG format online, free and private." path="/tools/image/png-to-jpg" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="convert" size={24} /></div>
            <div>
              <h1>PNG to JPG</h1>
              <p>Convert PNG images to JPG format right in your browser. Fast and private — files never leave your device.</p>
            </div>
          </div>
        </div>

        {!result ? (
          <div className="tool-box">
            <FileUpload
              accept="image/png"
              onFileChange={onFileChange}
              hint="Supported: PNG · Max 20MB"
              label="Click or drag & drop your PNG here"
            />

            <div style={{ marginTop: 20 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'var(--slate-700)', display:'block', marginBottom:6 }}>
                Quality: {Math.round(quality * 100)}%
              </label>
              <input type="range" min="0.5" max="1" step="0.01" value={quality} onChange={e => setQuality(parseFloat(e.target.value))} style={{ width:'100%' }} />
            </div>

            {error && (
              <div className="alert alert-error" style={{ marginTop: 14 }}>
                <Icon name="alertCircle" size={16} />{error}
              </div>
            )}

            <button className="btn btn-primary btn-lg btn-full" style={{ marginTop: 20 }} onClick={convert} disabled={busy}>
              {busy ? <><span className="spinner sp-sm" />Converting...</> : <><Icon name="convert" size={18} />Convert to JPG</>}
            </button>
          </div>
        ) : (
          <div className="dl-box">
            <div className="dl-icon"><Icon name="check" size={28} /></div>
            <h3>File Ready to Download!</h3>
            <p>Your JPG image is ready. New size: <strong>{fmtSize(result.size)}</strong></p>
            <div className="dl-actions">
              <a className="btn btn-success btn-lg" href={result.url} download="converted.jpg">
                <Icon name="download" size={18} />Download JPG
              </a>
              <button className="btn btn-outline btn-lg" onClick={reset}>
                <Icon name="refresh" size={18} />Convert Another
              </button>
            </div>
          </div>
        )}

        <div className="alert alert-info" style={{ marginTop: 14 }}>
          <Icon name="info" size={16} />
          <span>JPG files are smaller than PNG and great for photos. Note: JPG does not support transparency, so transparent areas become white. Everything happens in your browser.</span>
        </div>
        <ToolSeoContent tool="png-to-jpg" />
      </div>
    </div>
  </>
  );
}
